import React, { useState, useEffect } from 'react';

// API configuration
const API_BASE_URL = window.location.origin;

// API functions
const fetchClocks = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/clocks`);
    if (!response.ok) throw new Error('Failed to fetch clocks');
    return await response.json();
  } catch (error) {
    console.error('Error fetching clocks:', error);
    return [];
  }
};

const createClock = async (clockData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/clocks`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(clockData)
    });
    if (!response.ok) throw new Error('Failed to create clock');
    return await response.json();
  } catch (error) {
    console.error('Error creating clock:', error);
    throw error;
  }
};

// Settings API functions
const fetchSettings = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/settings`);
    if (!response.ok) throw new Error('Failed to fetch settings');
    return await response.json();
  } catch (error) {
    console.error('Error fetching settings:', error);
    return { style: 'seven-segment', color: 'green', font: 'console' };
  }
};

const saveSettings = async (settings) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/settings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(settings)
    });
    if (!response.ok) throw new Error('Failed to save settings');
    return await response.json();
  } catch (error) {
    console.error('Error saving settings:', error);
    throw error;
  }
};

const CONSOLE_FONT = '"SF Mono", "Monaco", "Consolas", "Roboto Mono", "Courier New", monospace';
const MONOSPACE_FONT = '"Courier New", Courier, monospace';
const PIXEL_FONT = '"Press Start 2P", "Courier New", monospace';
const SYSTEM_FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

const SevenSegmentDisplay = ({ value, size = 'large', color = '#00ff00', bgColor = '#003300' }) => {
  const scale = size === 'large' ? 2 : 0.6;
  const segments = {
    0: [1, 1, 1, 1, 1, 1, 0],
    1: [0, 1, 1, 0, 0, 0, 0],
    2: [1, 1, 0, 1, 1, 0, 1],
    3: [1, 1, 1, 1, 0, 0, 1],
    4: [0, 1, 1, 0, 0, 1, 1],
    5: [1, 0, 1, 1, 0, 1, 1],
    6: [1, 0, 1, 1, 1, 1, 1],
    7: [1, 1, 1, 0, 0, 0, 0],
    8: [1, 1, 1, 1, 1, 1, 1],
    9: [1, 1, 1, 1, 0, 1, 1]
  };
  
  const digit = segments[value] || [0, 0, 0, 0, 0, 0, 0];
  const width = 60 * scale;
  const height = 100 * scale;
  const thickness = 8 * scale;
  
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <rect x={thickness} y={0} width={width - thickness * 2} height={thickness} 
            fill={digit[0] ? color : bgColor} />
      <rect x={width - thickness} y={thickness} width={thickness} height={height / 2 - thickness} 
            fill={digit[1] ? color : bgColor} />
      <rect x={width - thickness} y={height / 2 + thickness} width={thickness} height={height / 2 - thickness * 2} 
            fill={digit[2] ? color : bgColor} />
      <rect x={thickness} y={height - thickness} width={width - thickness * 2} height={thickness} 
            fill={digit[3] ? color : bgColor} />
      <rect x={0} y={height / 2 + thickness} width={thickness} height={height / 2 - thickness * 2} 
            fill={digit[4] ? color : bgColor} />
      <rect x={0} y={thickness} width={thickness} height={height / 2 - thickness} 
            fill={digit[5] ? color : bgColor} />
      <rect x={thickness} y={height / 2 - thickness / 2} width={width - thickness * 2} height={thickness} 
            fill={digit[6] ? color : bgColor} />
    </svg>
  );
};

const SplitFlapDisplay = ({ value, size = 'large', color = '#00ff00' }) => {
  const scale = size === 'large' ? 2 : 0.6;
  const width = 60 * scale;
  const height = 100 * scale;
  
  return (
    <div style={{
      width: `${width}px`,
      height: `${height}px`,
      background: '#000',
      border: `${2 * scale}px solid ${color}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: `${60 * scale}px`,
      fontFamily: '"Courier New", Courier, monospace',
      fontWeight: 'bold',
      color: color,
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div style={{
        position: 'absolute',
        top: '50%',
        left: 0,
        right: 0,
        height: '1px',
        background: color,
        opacity: 0.3,
        zIndex: 10
      }} />
      {value}
    </div>
  );
};

const DotMatrixDisplay = ({ value, size = 'large', color = '#00ff00', bgColor = '#003300' }) => {
  const scale = size === 'large' ? 2 : 0.6;
  const patterns = {
    0: [[1,1,1,1],[1,0,0,1],[1,0,0,1],[1,0,0,1],[1,1,1,1]],
    1: [[0,0,1,0],[0,1,1,0],[0,0,1,0],[0,0,1,0],[0,1,1,1]],
    2: [[1,1,1,1],[0,0,0,1],[1,1,1,1],[1,0,0,0],[1,1,1,1]],
    3: [[1,1,1,1],[0,0,0,1],[1,1,1,1],[0,0,0,1],[1,1,1,1]],
    4: [[1,0,0,1],[1,0,0,1],[1,1,1,1],[0,0,0,1],[0,0,0,1]],
    5: [[1,1,1,1],[1,0,0,0],[1,1,1,1],[0,0,0,1],[1,1,1,1]],
    6: [[1,1,1,1],[1,0,0,0],[1,1,1,1],[1,0,0,1],[1,1,1,1]],
    7: [[1,1,1,1],[0,0,0,1],[0,0,1,0],[0,1,0,0],[0,1,0,0]],
    8: [[1,1,1,1],[1,0,0,1],[1,1,1,1],[1,0,0,1],[1,1,1,1]],
    9: [[1,1,1,1],[1,0,0,1],[1,1,1,1],[0,0,0,1],[1,1,1,1]]
  };
  
  const pattern = patterns[value] || patterns[0];
  const dotSize = 8 * scale;
  const gap = 3 * scale;
  
  return (
    <div style={{ display: 'inline-block' }}>
      {pattern.map((row, i) => (
        <div key={i} style={{ display: 'flex', gap: `${gap}px`, marginBottom: `${gap}px` }}>
          {row.map((dot, j) => (
            <div key={j} style={{
              width: `${dotSize}px`,
              height: `${dotSize}px`,
              borderRadius: '50%',
              background: dot ? color : bgColor
            }} />
          ))}
        </div>
      ))}
    </div>
  );
};

const TimeDigits = ({ digits, size, style, color = '#00ff00', bgColor = '#003300', font = CONSOLE_FONT }) => {
  const Component = style === 'seven-segment' ? SevenSegmentDisplay : 
                   style === 'split-flap' ? SplitFlapDisplay : DotMatrixDisplay;
  
  const isMobile = window.innerWidth < 768;
  const gap = size === 'large' ? (isMobile ? '5px' : '20px') : (isMobile ? '3px' : '8px');
  const colonSize = size === 'large' ? (isMobile ? '60px' : '120px') : (isMobile ? '20px' : '40px');
  
  return (
    <div style={{ 
      display: 'flex', 
      gap: gap, 
      alignItems: 'center', 
      justifyContent: 'center',
      flexWrap: 'wrap'
    }}>
      {digits.split('').map((digit, i) => 
        digit === ':' ? (
          <div key={i} style={{ 
            fontSize: colonSize,
            color: color,
            fontWeight: 'normal',
            margin: `0 ${isMobile ? '2px' : '10px'}`,
            fontFamily: font
          }}>:</div>
        ) : (
          <Component key={i} value={parseInt(digit)} size={size} color={color} bgColor={bgColor} />
        )
      )}
    </div>
  );
};

const ClockDisplay = ({ style, clockData, color = 'green', font = CONSOLE_FONT }) => {
  const [time, setTime] = useState({ hours: '00:00:00', years: '00', months: '00', days: '00' });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!clockData) return;

    const interval = setInterval(() => {
      const now = new Date();
      const targetDate = new Date(clockData.target_date ?? clockData.targetDate);
      const diff = now - targetDate;
      
      const totalSeconds = Math.floor(diff / 1000);
      const totalMinutes = Math.floor(totalSeconds / 60);
      const totalHours = Math.floor(totalMinutes / 60);
      const totalDays = Math.floor(totalHours / 24);
      
      const seconds = totalSeconds % 60;
      const minutes = totalMinutes % 60;
      const hours = totalHours % 24;
      const days = totalDays % 30;
      const months = Math.floor(totalDays / 30) % 12;
      const years = Math.floor(totalDays / 365);
      
      setTime({
        hours: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
        years: String(years).padStart(2, '0'),
        months: String(months).padStart(2, '0'),
        days: String(days).padStart(2, '0')
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [clockData]);

  const statement = clockData?.statement || 'Waiting for first clock-statement...';
  
  const colors = {
    green: { value: '#00ff00', bg: '#003300' },
    orange: { value: '#ff8800', bg: '#331100' },
    white: { value: '#ffffff', bg: '#333333' }
  };
  const bgColor = colors[color]?.bg || '#003300';
  const fgColor = colors[color]?.value || '#00ff00';

  return (
    <div style={{
      background: '#000',
      minHeight: '100vh',
      padding: isMobile ? '20px 10px' : '40px 20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: fgColor,
      fontFamily: font
    }}>
      <div style={{ 
        width: '100%',
        maxWidth: '1200px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: isMobile ? '10px' : '25px'
      }}>
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <TimeDigits digits={time.hours} size="large" style={style} color={fgColor} bgColor={bgColor} font={font} />
        </div>
        
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center',
          gap: isMobile ? '10px' : '30px',
          flexWrap: 'wrap'
        }}>
          <TimeDigits digits={time.years} size="small" style={style} color={fgColor} bgColor={bgColor} font={font} />
          <span style={{ color: fgColor, fontSize: isMobile ? '20px' : '40px' }}>:</span>
          <TimeDigits digits={time.months} size="small" style={style} color={fgColor} bgColor={bgColor} font={font} />
          <span style={{ color: fgColor, fontSize: isMobile ? '20px' : '40px' }}>:</span>
          <TimeDigits digits={time.days} size="small" style={style} color={fgColor} bgColor={bgColor} font={font} />
        </div>
        
        <div style={{
          textAlign: 'center',
          fontSize: isMobile ? '14px' : '18px',
          lineHeight: '1.6',
          maxWidth: '800px',
          marginTop: isMobile ? '20px' : '30px'
        }}>
          {statement}
        </div>
      </div>
    </div>
  );
};

const MiniClock = ({ clock, isMobile, color = '#00ff00', font = MONOSPACE_FONT }) => {
  const [time, setTime] = useState({ hours: '00:00:00' });

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const targetDate = new Date(clock.target_date ?? clock.targetDate);
      const diff = now - targetDate;
      
      const totalSeconds = Math.floor(diff / 1000);
      const totalMinutes = Math.floor(totalSeconds / 60);
      const totalHours = Math.floor(totalMinutes / 60);
      
      const seconds = totalSeconds % 60;
      const minutes = totalMinutes % 60;
      const hours = totalHours % 24;
      
      setTime({
        hours: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [clock.target_date ?? clock.targetDate]);

  return (
    <div 
      onClick={() => window.open(`${window.location.origin}${window.location.pathname}#clock/${clock.id}`, '_blank')}
      style={{
        border: `1px solid ${color}`,
        padding: isMobile ? '15px' : '20px',
        cursor: 'pointer',
        background: '#000',
        minHeight: isMobile ? '80px' : '100px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <div style={{ 
        fontSize: isMobile ? '20px' : '28px', 
        fontWeight: 'bold',
        textAlign: 'center',
        color: color,
        fontFamily: font
      }}>
        {time.hours}
      </div>
    </div>
  );
};

const ClockWallPage = ({ clocks, color = '#00ff00', font = MONOSPACE_FONT }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{
      background: '#000',
      minHeight: '100vh',
      padding: isMobile ? '15px' : '20px',
      color: color,
      fontFamily: font
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        paddingTop: isMobile ? '20px' : '40px'
      }}>
        <div style={{ fontSize: isMobile ? '20px' : '24px', marginBottom: '10px', textAlign: 'center' }}>
          {'\u003E CLOCK_WALL'}
        </div>
        <div style={{ fontSize: isMobile ? '12px' : '14px', marginBottom: isMobile ? '30px' : '40px', opacity: 0.7, textAlign: 'center' }}>
          {'\u003E Live clocks: ' + clocks.length}
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: isMobile ? '10px' : '15px'
        }}>
          {clocks.map((clock) => (
            <MiniClock key={clock.id} clock={clock} isMobile={isMobile} color={color} font={font} />
          ))}
        </div>
      </div>
    </div>
  );
};

const CalendarPage = ({ clocks, color = '#00ff00', font = MONOSPACE_FONT }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const getErasedDatesInMonth = () => {
    const erasedDates = {};
    clocks.forEach(clock => {
      const targetDate = new Date(clock.targetDate);
      if (targetDate.getFullYear() === year && targetDate.getMonth() === month) {
        const day = targetDate.getDate();
        if (!erasedDates[day]) erasedDates[day] = [];
        erasedDates[day].push(clock);
      }
    });
    return erasedDates;
  };

  const erasedDates = getErasedDatesInMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'];

  const calendarDays = [];
  for (let i = 0; i < firstDayOfMonth; i++) calendarDays.push(null);
  for (let day = 1; day <= daysInMonth; day++) calendarDays.push(day);

  return (
    <div style={{
      background: '#000',
      minHeight: '100vh',
      padding: isMobile ? '15px' : '20px',
      color: color,
      fontFamily: font
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', paddingTop: isMobile ? '20px' : '40px' }}>
        <div style={{ fontSize: isMobile ? '20px' : '24px', marginBottom: '10px', textAlign: 'center' }}>
          {'\u003E ERASED_DATES'}
        </div>
        <div style={{ fontSize: isMobile ? '12px' : '14px', marginBottom: isMobile ? '30px' : '40px', opacity: 0.7, textAlign: 'center' }}>
          {'\u003E ' + monthNames[month] + ' ' + year}
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px', gap: '15px' }}>
          <button onClick={() => setCurrentDate(new Date(year, month - 1, 1))} 
            style={{ padding: '10px 15px', background: '#000', color: color, border: `1px solid ${color}`, cursor: 'pointer', fontFamily: font }}>
            {'<'}
          </button>
          <button onClick={() => setCurrentDate(new Date())} 
            style={{ padding: '10px 15px', background: '#000', color: color, border: `1px solid ${color}`, cursor: 'pointer', fontFamily: font }}>
            [TODAY]
          </button>
          <button onClick={() => setCurrentDate(new Date(year, month + 1, 1))} 
            style={{ padding: '10px 15px', background: '#000', color: color, border: `1px solid ${color}`, cursor: 'pointer', fontFamily: font }}>
            {'>'}
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: isMobile ? '5px' : '10px' }}>
          {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(day => (
            <div key={day} style={{ textAlign: 'center', padding: '10px', fontSize: '12px', fontWeight: 'bold', borderBottom: `1px solid ${color}` }}>
              {day}
            </div>
          ))}

          {calendarDays.map((day, index) => {
            const hasErasures = day && erasedDates[day] && erasedDates[day].length > 0;
            return (
              <div key={index} style={{
                border: `1px solid ${color}`,
                minHeight: isMobile ? '60px' : '80px',
                padding: '8px',
                background: hasErasures ? 'rgba(0, 255, 0, 0.1)' : '#000',
                cursor: hasErasures ? 'pointer' : 'default'
              }}
              onClick={() => hasErasures && window.open(`${window.location.origin}${window.location.pathname}#clock/${erasedDates[day][0].id}`, '_blank')}
              >
                {day && <div style={{ fontSize: '16px', color: color }}>{day}</div>}
                {hasErasures && <div style={{ fontSize: '9px', opacity: 0.7, marginTop: '5px' }}>{erasedDates[day].length}x</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const CreationForm = ({ onSubmit, isSubmitting, color = '#00ff00', font = MONOSPACE_FONT }) => {
  const [statement, setStatement] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [email, setEmail] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = () => {
    if (!statement || !date || !time) {
      alert('Please fill statement, date, and time');
      return;
    }
    
    const targetDate = new Date(`${date}T${time}`);
    if (targetDate > new Date()) {
      alert('You cannot erase a date in the future. Only past moments can be erased.');
      return;
    }
    
    onSubmit({ statement, targetDate: targetDate.toISOString(), email });
  };

  return (
    <div style={{
      background: '#000',
      minHeight: '100vh',
      padding: isMobile ? '15px' : '20px',
      color: color,
      fontFamily: font,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <style>
        {`
          input::placeholder, textarea::placeholder {
            color: ${color};
            opacity: 0.5;
          }
          input::-webkit-calendar-picker-indicator {
            filter: ${color === '#00ff00' ? 'invert(1) hue-rotate(60deg)' : 
                     color === '#ff8800' ? 'invert(1) hue-rotate(180deg)' : 
                     'invert(1)'};
          }
        `}
      </style>
      <div style={{ maxWidth: '600px', width: '100%' }}>
        <div style={{ fontSize: isMobile ? '20px' : '24px', marginBottom: '10px' }}>
          {'\u003E TIME_MACHINE'}
        </div>
        <div style={{ fontSize: isMobile ? '12px' : '14px', marginBottom: isMobile ? '30px' : '40px', opacity: 0.7 }}>
          {'\u003E Create a clock that counts backwards from a moment you want to erase'}
        </div>

        <div style={{ marginBottom: isMobile ? '25px' : '30px' }}>
          <div style={{ marginBottom: '10px', fontSize: isMobile ? '13px' : '14px' }}>
            {'\u003E YOUR_STATEMENT:'}
          </div>
          <textarea
            value={statement}
            onChange={(e) => setStatement(e.target.value)}
            placeholder="The moment everything changed..."
            style={{
              width: '100%',
              minHeight: isMobile ? '100px' : '120px',
              padding: isMobile ? '12px' : '15px',
              background: '#000',
              border: `1px solid ${color}`,
              color: color,
              fontSize: isMobile ? '14px' : '16px',
              fontFamily: font,
              resize: 'vertical',
              boxSizing: 'border-box',
              outline: 'none'
            }}
            maxLength={500}
          />
        </div>

        <div style={{ marginBottom: isMobile ? '25px' : '30px' }}>
          <div style={{ marginBottom: '10px', fontSize: isMobile ? '13px' : '14px' }}>
            {'\u003E DATE_TO_ERASE:'}
          </div>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
            style={{
              width: '100%',
              padding: isMobile ? '12px' : '15px',
              background: '#000',
              border: `1px solid ${color}`,
              color: color,
              fontSize: isMobile ? '14px' : '16px',
              fontFamily: font,
              boxSizing: 'border-box',
              outline: 'none',
              colorScheme: 'dark'
            }}
          />
        </div>

        <div style={{ marginBottom: isMobile ? '25px' : '30px' }}>
          <div style={{ marginBottom: '10px', fontSize: isMobile ? '13px' : '14px' }}>
            {'\u003E TIME_TO_ERASE:'}
          </div>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            style={{
              width: '100%',
              padding: isMobile ? '12px' : '15px',
              background: '#000',
              border: `1px solid ${color}`,
              color: color,
              fontSize: isMobile ? '14px' : '16px',
              fontFamily: font,
              boxSizing: 'border-box',
              outline: 'none',
              colorScheme: 'dark'
            }}
          />
        </div>

        <div style={{ marginBottom: isMobile ? '35px' : '40px' }}>
          <div style={{ marginBottom: '10px', fontSize: isMobile ? '13px' : '14px' }}>
            {'\u003E EMAIL (optional):'}
          </div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            style={{
              width: '100%',
              padding: isMobile ? '12px' : '15px',
              background: '#000',
              border: `1px solid ${color}`,
              color: color,
              fontSize: isMobile ? '14px' : '16px',
              fontFamily: font,
              boxSizing: 'border-box',
              outline: 'none'
            }}
          />
        </div>

        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          style={{
            width: '100%',
            padding: isMobile ? '18px' : '20px',
            background: '#000',
            color: color,
            border: `2px solid ${color}`,
            fontSize: isMobile ? '16px' : '18px',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            fontFamily: font,
            opacity: isSubmitting ? 0.5 : 1
          }}
        >
          {isSubmitting ? '[CREATING...]' : '[ERASE TIME]'}
        </button>
      </div>
    </div>
  );
};

const ArchivePage = ({ clocks, onSelectClock, color = '#00ff00', font = MONOSPACE_FONT }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{
      background: '#000',
      minHeight: '100vh',
      padding: isMobile ? '15px' : '20px',
      color: color,
      fontFamily: font
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', paddingTop: isMobile ? '20px' : '40px' }}>
        <div style={{ fontSize: isMobile ? '20px' : '24px', marginBottom: '10px' }}>
          {'\u003E ARCHIVE'}
        </div>
        <div style={{ fontSize: isMobile ? '12px' : '14px', marginBottom: isMobile ? '30px' : '40px', opacity: 0.7 }}>
          {'\u003E Total clocks: ' + clocks.length}
        </div>

        <div style={{ display: 'grid', gap: isMobile ? '15px' : '20px' }}>
          {clocks.map((clock) => (
            <div
              key={clock.id}
              onClick={() => onSelectClock(clock.id)}
              style={{
                padding: isMobile ? '15px' : '20px',
                border: `1px solid ${color}`,
                cursor: 'pointer'
              }}
            >
              <div style={{ fontSize: isMobile ? '11px' : '12px', marginBottom: '10px', opacity: 0.5 }}>
                {'\u003E Created: ' + new Date(clock.created_at ?? clock.createdAt).toLocaleString()}
              </div>
              <div style={{ fontSize: isMobile ? '12px' : '14px', marginBottom: '10px' }}>
                {'\u003E Target: ' + new Date(clock.target_date ?? clock.targetDate).toLocaleString()}
              </div>
              <div style={{ fontSize: isMobile ? '14px' : '16px', lineHeight: '1.6' }}>
                {clock.statement}
              </div>
            </div>
          ))}
          
          {clocks.length === 0 && (
            <div style={{ padding: isMobile ? '30px' : '40px', textAlign: 'center', opacity: 0.5 }}>
              {'\u003E No clocks created yet'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const LedgerPage = ({ clocks, color = '#00ff00', font = MONOSPACE_FONT }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{
      background: '#000',
      minHeight: '100vh',
      padding: isMobile ? '15px' : '20px',
      color: color,
      fontFamily: font
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', paddingTop: isMobile ? '20px' : '40px' }}>
        <div style={{ fontSize: isMobile ? '20px' : '24px', marginBottom: '10px' }}>
          {'\u003E BLOCKCHAIN_LEDGER'}
        </div>
        <div style={{ fontSize: isMobile ? '12px' : '14px', marginBottom: isMobile ? '30px' : '40px', opacity: 0.7 }}>
          {'\u003E Immutable record of all clock-statements'}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '15px' : '20px' }}>
          {clocks.map((clock, index) => (
            <div key={clock.id} style={{
              padding: isMobile ? '12px' : '20px',
              border: `1px solid ${color}`,
              fontSize: isMobile ? '10px' : '12px',
              fontFamily: font
            }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {JSON.stringify({ block: index, id: clock.id, targetDate: clock.targetDate, statement: clock.statement.substring(0, 50) + '...' }, null, 2)}
              </pre>
            </div>
          ))}
          
          {clocks.length === 0 && (
            <div style={{ padding: isMobile ? '30px' : '40px', textAlign: 'center', opacity: 0.5 }}>
              {'\u003E Genesis block pending...'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = ({ currentStyle, onStyleChange, onLogout, currentColor, onColorChange, currentFont, onFontChange }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [previewTime, setPreviewTime] = useState({ hours: '12:34:56', years: '05', months: '08', days: '23' });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const seconds = now.getSeconds();
      
      setPreviewTime({
        hours: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
        years: '05',
        months: '08',
        days: '23'
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  const colors = {
    green: { value: '#00ff00', name: 'GREEN_TERMINAL', bg: '#003300' },
    orange: { value: '#ff8800', name: 'AMBER_MONITOR', bg: '#331100' },
    white: { value: '#ffffff', name: 'WHITE_PHOSPHOR', bg: '#333333' }
  };
  
  const fonts = {
    console: { value: CONSOLE_FONT, name: 'CONSOLE_MODERN' },
    monospace: { value: MONOSPACE_FONT, name: 'COURIER_CLASSIC' },
    pixel: { value: PIXEL_FONT, name: 'PIXEL_RETRO' },
    system: { value: SYSTEM_FONT, name: 'SYSTEM_CLEAN' }
  };

  const activeColor = colors[currentColor] || colors.green;
  const activeFont = fonts[currentFont] || fonts.console;

  return (
    <div style={{
      background: '#000',
      minHeight: '100vh',
      padding: isMobile ? '15px' : '20px',
      color: activeColor.value,
      fontFamily: activeFont.value
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: isMobile ? '30px' : '40px',
          paddingBottom: isMobile ? '15px' : '20px',
          borderBottom: `1px solid ${activeColor.value}`
        }}>
          <div style={{ fontSize: isMobile ? '16px' : '18px' }}>{'\u003E ADMIN_DASHBOARD'}</div>
          <button onClick={onLogout} style={{
              padding: isMobile ? '6px 12px' : '8px 16px',
              background: '#000',
              color: activeColor.value,
              border: `1px solid ${activeColor.value}`,
              cursor: 'pointer',
              fontSize: isMobile ? '12px' : '14px',
              fontFamily: activeFont.value
            }}>
            [LOGOUT]
          </button>
        </div>

        <div style={{ marginBottom: isMobile ? '30px' : '40px' }}>
          <div style={{ fontSize: isMobile ? '14px' : '16px', marginBottom: isMobile ? '15px' : '20px' }}>{'\u003E COLOR_SCHEME'}</div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '12px' : '15px' }}>
            {Object.entries(colors).map(([key, color]) => (
              <button 
                key={key} 
                onClick={() => onColorChange(key)}
                style={{
                  padding: isMobile ? '15px' : '20px',
                  background: '#000',
                  color: activeColor.value,
                  border: currentColor === key ? `2px solid ${activeColor.value}` : `1px solid ${activeColor.value}`,
                  cursor: 'pointer',
                  fontSize: isMobile ? '13px' : '14px',
                  textAlign: 'left',
                  fontFamily: activeFont.value,
                  opacity: currentColor === key ? 1 : 0.6
                }}>
                <span style={{ color: color.value }}>{currentColor === key ? '[X]' : '[ ]'} {color.name}</span>
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: isMobile ? '30px' : '40px' }}>
          <div style={{ fontSize: isMobile ? '14px' : '16px', marginBottom: isMobile ? '15px' : '20px' }}>{'\u003E FONT_STYLE'}</div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '12px' : '15px' }}>
            {Object.entries(fonts).map(([key, font]) => (
              <button key={key} onClick={() => onFontChange(key)} style={{
                    padding: isMobile ? '15px' : '20px',
                    background: '#000',
                    color: activeColor.value,
                    border: currentFont === key ? `2px solid ${activeColor.value}` : `1px solid ${activeColor.value}`,
                    cursor: 'pointer',
                    fontSize: isMobile ? '13px' : '14px',
                    textAlign: 'left',
                    fontFamily: font.value,
                    opacity: currentFont === key ? 1 : 0.6
                  }}>
                {currentFont === key ? '[X]' : '[ ]'} {font.name}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: isMobile ? '30px' : '40px' }}>
          <div style={{ fontSize: isMobile ? '14px' : '16px', marginBottom: isMobile ? '15px' : '20px' }}>{'\u003E CLOCK_DISPLAY_STYLE'}</div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '12px' : '15px' }}>
            {['seven-segment', 'split-flap', 'dot-matrix'].map((styleOption) => (
              <button key={styleOption} onClick={() => onStyleChange(styleOption)} style={{
                    padding: isMobile ? '15px' : '20px',
                    background: '#000',
                    color: activeColor.value,
                    border: currentStyle === styleOption ? `2px solid ${activeColor.value}` : `1px solid ${activeColor.value}`,
                    cursor: 'pointer',
                    fontSize: isMobile ? '13px' : '14px',
                    textAlign: 'left',
                    fontFamily: activeFont.value,
                    opacity: currentStyle === styleOption ? 1 : 0.6
                  }}>
                {currentStyle === styleOption ? '[X]' : '[ ]'} {styleOption.toUpperCase().replace('-', '_')}
              </button>
            ))}
          </div>
        </div>

        <div style={{ 
          marginTop: isMobile ? '40px' : '60px',
          padding: isMobile ? '20px' : '30px',
          border: `1px solid ${activeColor.value}`,
          background: '#000'
        }}>
          <div style={{ fontSize: isMobile ? '14px' : '16px', marginBottom: isMobile ? '20px' : '30px', textAlign: 'center' }}>
            {'\u003E PREVIEW'}
          </div>
          
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: isMobile ? '10px' : '20px'
          }}>
            <TimeDigits 
              digits={previewTime.hours} 
              size="large" 
              style={currentStyle} 
              color={activeColor.value} 
              bgColor={activeColor.bg}
              font={activeFont.value}
            />
            
            <div style={{ 
              display: 'flex', 
              gap: isMobile ? '8px' : '15px',
              alignItems: 'center'
            }}>
              <TimeDigits 
                digits={previewTime.years} 
                size="small" 
                style={currentStyle} 
                color={activeColor.value} 
                bgColor={activeColor.bg}
                font={activeFont.value}
              />
              <span style={{ color: activeColor.value, fontSize: isMobile ? '16px' : '24px' }}>:</span>
              <TimeDigits 
                digits={previewTime.months} 
                size="small" 
                style={currentStyle} 
                color={activeColor.value} 
                bgColor={activeColor.bg}
                font={activeFont.value}
              />
              <span style={{ color: activeColor.value, fontSize: isMobile ? '16px' : '24px' }}>:</span>
              <TimeDigits 
                digits={previewTime.days} 
                size="small" 
                style={currentStyle} 
                color={activeColor.value} 
                bgColor={activeColor.bg}
                font={activeFont.value}
              />
            </div>
            
            <div style={{
              textAlign: 'center',
              fontSize: isMobile ? '11px' : '13px',
              marginTop: isMobile ? '15px' : '20px',
              color: activeColor.value,
              fontFamily: activeFont.value,
              opacity: 0.7
            }}>
              The moment everything changed...
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LoginScreen = ({ onLogin, color = '#00ff00', font = MONOSPACE_FONT }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = () => {
    // Use the admin password from environment or fallback to default
    const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD || 'ERASE2025';
    if (code === adminPassword) {
      onLogin();
    } else {
      setError('\u003E ERROR: Invalid access code');
      setCode('');
    }
  };

  return (
    <div style={{
      background: '#000',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: font,
      padding: isMobile ? '15px' : '20px'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '500px',
        padding: isMobile ? '30px 20px' : '40px',
        border: `1px solid ${color}`,
        background: '#000'
      }}>
        <div style={{ color: color, fontSize: isMobile ? '16px' : '18px', marginBottom: isMobile ? '25px' : '30px' }}>
          {'\u003E ACCESS_REQUIRED'}
        </div>
        
        <div>
          <div style={{ marginBottom: '10px', color: color, fontSize: isMobile ? '13px' : '14px' }}>
            {'\u003E ENTER_CODE:'}
          </div>
          <input
            type="password"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
            placeholder="****************"
            style={{
              width: '100%',
              padding: isMobile ? '12px' : '15px',
              background: '#000',
              border: `1px solid ${color}`,
              color: color,
              fontSize: isMobile ? '14px' : '16px',
              marginBottom: '15px',
              fontFamily: font,
              boxSizing: 'border-box'
            }}
          />
          
          {error && <div style={{ color: color, marginBottom: '15px', fontSize: isMobile ? '13px' : '14px' }}>{error}</div>}
          
          <button onClick={handleSubmit} style={{
              width: '100%',
              padding: isMobile ? '12px' : '15px',
              background: '#000',
              color: color,
              border: `1px solid ${color}`,
              fontSize: isMobile ? '14px' : '16px',
              cursor: 'pointer',
              fontFamily: font
            }}>
            [AUTHENTICATE]
          </button>
          
          <div style={{
            textAlign: 'center',
            marginTop: '30px',
            paddingTop: '20px',
            borderTop: `1px solid ${color}`,
            opacity: 0.6
          }}>
            <a 
              href="https://www.fanguitoestudio.com/time_machine" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                color: color,
                textDecoration: 'none',
                fontSize: isMobile ? '12px' : '13px',
                fontFamily: font,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.opacity = '1'}
              onMouseLeave={(e) => e.target.style.opacity = '0.6'}
            >
              Rodolfo Peraza Studio
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function App() {
  const [view, setView] = useState(() => {
    const path = window.location.hash.replace('#', '');
    if (path === 'admin') return 'login';
    if (path === 'create') return 'create';
    if (path === 'archive') return 'archive';
    if (path === 'wall') return 'wall';
    if (path === 'calendar') return 'calendar';
    if (path === 'ledger') return 'ledger';
    if (path.startsWith('clock/')) return 'single-clock';
    return 'public';
  });
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [style, setStyle] = useState('seven-segment');
  const [color, setColor] = useState('green');
  const [font, setFont] = useState('console');
  const [clocks, setClocks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentClockId, setCurrentClockId] = useState(() => {
    const path = window.location.hash.replace('#', '');
    if (path.startsWith('clock/')) {
      return path.replace('clock/', '');
    }
    return null;
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const colors = {
    green: { value: '#00ff00', bg: '#003300' },
    orange: { value: '#ff8800', bg: '#331100' },
    white: { value: '#ffffff', bg: '#333333' }
  };
  
  const fonts = {
    console: CONSOLE_FONT,
    monospace: MONOSPACE_FONT,
    pixel: PIXEL_FONT,
    system: SYSTEM_FONT
  };
  
  const activeColor = colors[color] || colors.green;
  const activeFont = fonts[font] || fonts.console;

  // Load clocks and settings from API on component mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Load clocks and settings in parallel
        const [clocksData, settingsData] = await Promise.all([
          fetchClocks(),
          fetchSettings()
        ]);
        
        setClocks(clocksData);
        
        // Set settings from API or fallback to defaults
        setStyle(settingsData.style || 'seven-segment');
        setColor(settingsData.color || 'green');
        setFont(settingsData.font || 'console');
        
      } catch (error) {
        console.error('Failed to load data:', error);
        // Fallback to default settings
        setStyle('seven-segment');
        setColor('green');
        setFont('console');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);
  
  useEffect(() => {
    const handleHashChange = () => {
      const path = window.location.hash.replace('#', '');
      if (path === 'admin') {
        if (isAuthenticated) {
          setView('admin');
        } else {
          setView('login');
        }
      } else if (path === 'create') {
        setView('create');
      } else if (path === 'archive') {
        setView('archive');
      } else if (path === 'wall') {
        setView('wall');
      } else if (path === 'calendar') {
        setView('calendar');
      } else if (path === 'ledger') {
        setView('ledger');
      } else if (path.startsWith('clock/')) {
        setView('single-clock');
        setCurrentClockId(path.replace('clock/', ''));
      } else {
        setView('public');
      }
    };
    
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [isAuthenticated]);
  
  const handleLogin = () => {
    setIsAuthenticated(true);
    setView('admin');
  };
  
  const handleLogout = () => {
    setIsAuthenticated(false);
    window.location.hash = '';
  };
  
  const handleStyleChange = async (newStyle) => {
    setStyle(newStyle);
    try {
      await saveSettings({ style: newStyle, color, font });
    } catch (error) {
      console.error('Failed to save style setting:', error);
    }
  };
  
  const handleColorChange = async (newColor) => {
    setColor(newColor);
    try {
      await saveSettings({ style, color: newColor, font });
    } catch (error) {
      console.error('Failed to save color setting:', error);
    }
  };
  
  const handleFontChange = async (newFont) => {
    setFont(newFont);
    try {
      await saveSettings({ style, color, font: newFont });
    } catch (error) {
      console.error('Failed to save font setting:', error);
    }
  };
  
  const handleCreateClock = async (clockData) => {
    setIsSubmitting(true);
    
    try {
      const clockPayload = {
        id: Date.now().toString(36) + Math.random().toString(36).substr(2),
        statement: clockData.statement,
        targetDate: clockData.targetDate,
        email: clockData.email
      };

      const newClock = await createClock(clockPayload);
      
      // Update local state with the new clock
      setClocks(prevClocks => [newClock, ...prevClocks]);
      
      // Redirect to the main clock view
      window.location.hash = '';
    } catch (error) {
      console.error('Failed to create clock:', error);
      alert('Failed to create clock. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleSelectClock = (clockId) => {
    window.location.hash = `clock/${clockId}`;
  };
  
  const getCurrentClock = () => {
    if (view === 'single-clock' && currentClockId) {
      const foundClock = clocks.find(c => c.id === currentClockId);
      // If we haven't found the clock yet but we have a currentClockId, 
      // don't fall back to clocks[0] - wait for the correct clock to load
      return foundClock || null;
    }
    return clocks[0] || null;
  };
  
  const currentClock = getCurrentClock();
  
  return (
    <div style={{ background: '#000', minHeight: '100vh', paddingBottom: '60px', overflow: 'hidden', width: '100%', boxSizing: 'border-box' }}>
      <style>
        {`
          body, html {
            margin: 0;
            padding: 0;
            overflow-x: hidden;
            width: 100%;
          }
          * {
            box-sizing: border-box;
          }
        `}
      </style>
      <div style={{ minHeight: 'calc(100vh - 60px)', overflowX: 'hidden', width: '100%' }}>
        {isLoading && (view === 'public' || view === 'single-clock') && (
          <div style={{
            background: '#000',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: activeColor.value,
            fontFamily: activeFont,
            fontSize: '18px'
          }}>
            {'\u003E Loading clocks...'}
          </div>
        )}
        {!isLoading && view === 'public' && <ClockDisplay style={style} clockData={currentClock} color={color} font={activeFont} />}
        {!isLoading && view === 'single-clock' && <ClockDisplay style={style} clockData={currentClock} color={color} font={activeFont} />}
        {view === 'create' && <CreationForm onSubmit={handleCreateClock} isSubmitting={isSubmitting} color={activeColor.value} font={activeFont} />}
        {view === 'archive' && <ArchivePage clocks={clocks} onSelectClock={handleSelectClock} color={activeColor.value} font={activeFont} />}
        {view === 'wall' && <ClockWallPage clocks={clocks} color={activeColor.value} font={activeFont} />}
        {view === 'calendar' && <CalendarPage clocks={clocks} color={activeColor.value} font={activeFont} />}
        {view === 'ledger' && <LedgerPage clocks={clocks} color={activeColor.value} font={activeFont} />}
        {view === 'login' && !isAuthenticated && <LoginScreen onLogin={handleLogin} color={activeColor.value} font={activeFont} />}
        {view === 'admin' && isAuthenticated && <AdminDashboard currentStyle={style} onStyleChange={handleStyleChange} onLogout={handleLogout} currentColor={color} onColorChange={handleColorChange} currentFont={font} onFontChange={handleFontChange} />}
      </div>

      <footer style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: '#000',
        padding: '15px 20px',
        display: 'flex',
        justifyContent: 'center',
        gap: window.innerWidth < 768 ? '15px' : '40px',
        flexWrap: 'wrap',
        zIndex: 1000,
        fontFamily: activeFont
      }}>
        <button onClick={() => window.location.hash = ''} style={{ color: activeColor.value, textDecoration: view === 'public' || view === 'single-clock' ? 'underline' : 'none', fontSize: '14px', padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: activeFont }}>
          CLOCK
        </button>
        <button onClick={() => window.location.hash = 'create'} style={{ color: activeColor.value, textDecoration: view === 'create' ? 'underline' : 'none', fontSize: '14px', padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: activeFont }}>
          TIME_MACHINE
        </button>
        <button onClick={() => window.location.hash = 'ledger'} style={{ color: activeColor.value, textDecoration: view === 'ledger' ? 'underline' : 'none', fontSize: '14px', padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: activeFont }}>
          LEDGER
        </button>
        <button onClick={() => window.location.hash = 'calendar'} style={{ color: activeColor.value, textDecoration: view === 'calendar' ? 'underline' : 'none', fontSize: '14px', padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: activeFont }}>
          CALENDAR
        </button>
        <button onClick={() => window.location.hash = 'archive'} style={{ color: activeColor.value, textDecoration: view === 'archive' ? 'underline' : 'none', fontSize: '14px', padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: activeFont }}>
          ARCHIVE
        </button>
        <button onClick={() => window.location.hash = 'wall'} style={{ color: activeColor.value, textDecoration: view === 'wall' ? 'underline' : 'none', fontSize: '14px', padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: activeFont }}>
          WALL
        </button>
        <button onClick={() => window.location.hash = 'admin'} style={{ color: activeColor.value, textDecoration: view === 'login' || view === 'admin' ? 'underline' : 'none', fontSize: '14px', padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: activeFont, opacity: 0.5 }}>
          GODCLOCK
        </button>
      </footer>
    </div>
  );
}